import type { FC } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import type { InlineCommentCreateComponentProps } from '@atlaskit/editor-plugins/annotation';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ADD_INLINE_COMMENT_EXPERIENCE,
	ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	startEditorExperiences,
} from '@confluence/experience-tracker';
import { findMatches, indexTextNodes, IGNORE_SELECTORS } from '@confluence/highlight-actions';
import { useAnnotations } from '@confluence/annotation-provider-store';
import {
	InlineCommentMode,
	InlineCommentFramework,
} from '@confluence/inline-comments-utils/entry-points/enum';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { CommentCreationLocation } from '@confluence/inline-comments-queries';
import { fg } from '@confluence/feature-gating';

import { CreateComment } from '../CreateComment';
import type { SelectionOptions } from '../renderer/SelectionComponent';

export type CreateComponentProps = {
	pageId: string;
	closeObjectSidebar?: () => void;
};

export const CreateComponent: FC<InlineCommentCreateComponentProps & CreateComponentProps> = ({
	pageId,
	onCreate,
	onClose,
	dom: selectionElement,
	textSelection,
	inlineNodeTypes,
	isOpeningMediaCommentFromToolbar = false,
	closeObjectSidebar,
	isOffline,
}) => {
	const { editorCreateProps } = useAnnotations();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const editorNode = useRef<Element | null>(null);
	const indexFound = useRef(0);
	const textMatches = useRef(0);

	const isLivePage = useIsCurrentPageLive();

	// Required to close the sidebar once we create a new standalone comment
	useEffect(() => {
		closeObjectSidebar &&
			editorCreateProps &&
			fg('confluence-frontend-unmount-standalone-comment') &&
			closeObjectSidebar();
	}, [closeObjectSidebar, editorCreateProps]);

	useEffect(() => {
		!fg('confluence-frontend-unmount-standalone-comment') &&
			closeObjectSidebar &&
			closeObjectSidebar();
		startEditorExperiences(
			{
				name: ADD_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					mode: isLivePage ? InlineCommentMode.LIVE : InlineCommentMode.EDIT,
					framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				},
			},
			{
				name: ADD_INLINE_COMMENT_LOAD_EXPERIENCE,
			},
			{
				name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
			},
			true,
		);

		// both editor and inline comment sidebar share the same class, target the element that isn't the sidebar
		const editorNodes = Array.from(
			document.querySelectorAll('.ak-editor-content-area > .ProseMirror'),
		);

		if (editorNodes.length) {
			const foundEditorNode = editorNodes.find(
				(element) => !element.closest('#editor-create-sidebar'),
			);
			if (foundEditorNode) {
				editorNode.current = foundEditorNode;
			}
		}

		if (selectionElement && editorNode.current && textSelection) {
			const ignoredNodes: HTMLElement[] = Array.from(
				editorNode.current.querySelectorAll(IGNORE_SELECTORS),
			);

			const { text, nodesStartPos, nodes } = indexTextNodes(editorNode.current, ignoredNodes);

			const { index, numMatches } = findMatches(
				text,
				textSelection,
				// we prefer the TextNode rather than <p> or headings
				selectionElement.parentNode ? selectionElement.parentNode : selectionElement,
				// default to 0 since the Editor wraps the selection in a <span> now
				0,
				nodesStartPos,
				nodes,
			);

			indexFound.current = index;
			textMatches.current = numMatches;

			if (fg('confluence_frontend_mismatch_analytic_event')) {
				// Measure the newly created annotation element text length and the selection text length
				const draftAnnotationElems = document.querySelectorAll('.ak-editor-annotation-draft');
				if (draftAnnotationElems.length > 0) {
					let draftTextContent = '';

					draftAnnotationElems.forEach((draftAnnotationElem) => {
						draftTextContent += draftAnnotationElem.textContent;
					});

					// If they don't line up, send an event tracking it
					if (textSelection.length !== draftTextContent.length) {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'mismatched',
								actionSubject: 'draftText',
								actionSubjectId: 'editPage',
								attributes: {
									contentId: pageId,
									originalSelectionLength: textSelection.length,
									draftAnnotationLength: draftTextContent.length,
									isLivePage,
								},
								source: 'annotationProvider',
							},
						}).fire();
					}
				}
			}
		}
	}, [
		selectionElement,
		textSelection,
		isLivePage,
		pageId,
		closeObjectSidebar,
		createAnalyticsEvent,
		editorCreateProps,
	]);

	const selectionOptions: SelectionOptions = useMemo(
		() => ({
			originalSelection: textSelection!,
			numMatches: textMatches.current,
			matchIndex: indexFound.current,
			inlineNodeTypes,
			createdFrom: isLivePage ? CommentCreationLocation.LIVE : CommentCreationLocation.EDITOR,
		}),
		[textSelection, textMatches, indexFound, isLivePage, inlineNodeTypes],
	);

	return (
		<CreateComment
			pageId={pageId}
			annotationElement={selectionElement}
			selectionOptions={selectionOptions}
			onClose={onClose}
			onCreate={onCreate}
			isEditor
			isOpeningMediaCommentFromToolbar={isOpeningMediaCommentFromToolbar}
			isOffline={isOffline}
		/>
	);
};
